<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import parsePhoneNumber from 'libphonenumber-js'
import { useForm } from 'vee-validate'
import { z } from 'zod'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form'

export interface ManualEntryValues {
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
}

export interface RoleOption {
  value: string
  label: string
}

const props = withDefaults(defineProps<{
  roles: RoleOption[]
  defaultRole: string
  mode?: 'email' | 'emailOrPhone'
  initialContact?: string
}>(), {
  mode: 'email',
  initialContact: '',
})

const emit = defineEmits<{
  submit: [values: ManualEntryValues, role: string]
  cancel: []
}>()

const role = defineModel<string>('role', { default: '' })

// Email-only schema
const emailSchema = toTypedSchema(z.object({
  first_name: z.string().min(1, 'Required').max(255),
  last_name: z.string().min(1, 'Required').max(255),
  contact: z.string().min(1, 'Required').email('Enter a valid email'),
}))

// Email or phone schema
const phoneFieldSchema = z.string().min(1, 'Enter a valid email or phone').transform((value, ctx) => {
  try {
    const phoneNumber = parsePhoneNumber(value, { defaultCountry: 'US' })
    if (!phoneNumber?.isValid()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid email or phone' })
      return z.NEVER
    }
    return phoneNumber.number as string
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid email or phone' })
    return z.NEVER
  }
})
const emailFieldSchema = z.string().min(1, 'Enter a valid email or phone').email('Enter a valid email or phone')
const emailOrPhoneField = z.union([emailFieldSchema, phoneFieldSchema]).refine(
  v => v !== null && v !== undefined && v !== '',
  { message: 'Enter a valid email or phone' },
)

const emailOrPhoneSchema = toTypedSchema(z.object({
  first_name: z.string().min(1, 'Required').max(255),
  last_name: z.string().min(1, 'Required').max(255),
  contact: emailOrPhoneField,
}))

const validationSchema = props.mode === 'emailOrPhone' ? emailOrPhoneSchema : emailSchema
const contactLabel = props.mode === 'emailOrPhone' ? 'Email or phone' : 'Email'

const { handleSubmit, submitCount } = useForm({
  validationSchema,
  initialValues: { first_name: '', last_name: '', contact: props.initialContact },
})

const onSubmit = handleSubmit((values) => {
  const contactVal = values.contact as string
  let email: string | null = null
  let phone: string | null = null

  if (props.mode === 'emailOrPhone' && !contactVal.includes('@')) {
    phone = contactVal
  } else {
    email = contactVal
  }

  emit('submit', {
    first_name: values.first_name,
    last_name: values.last_name,
    email,
    phone,
  }, role.value || props.defaultRole)
})

const onCancel = () => {
  role.value = props.defaultRole
  emit('cancel')
}

// Initialize role
if (!role.value) {
  role.value = props.defaultRole
}
</script>

<template>
  <div class="space-y-2 rounded-md bg-muted p-3">
    <p class="text-base font-semibold">Add new user</p>
    <FormField
      name="first_name"
      :validate-on-blur="false"
      :validate-on-change="false"
      :validate-on-model-update="submitCount > 0"
      v-slot="{ componentField }"
    >
      <FormItem>
        <FormLabel>First name</FormLabel>
        <FormControl>
          <Input v-bind="componentField" data-testid="invite-first-name-input" />
        </FormControl>
      </FormItem>
    </FormField>
    <FormField
      name="last_name"
      :validate-on-blur="false"
      :validate-on-change="false"
      :validate-on-model-update="submitCount > 0"
      v-slot="{ componentField }"
    >
      <FormItem>
        <FormLabel>Last name</FormLabel>
        <FormControl>
          <Input v-bind="componentField" data-testid="invite-last-name-input" />
        </FormControl>
      </FormItem>
    </FormField>
    <FormField
      name="contact"
      :validate-on-blur="false"
      :validate-on-change="false"
      :validate-on-model-update="submitCount > 0"
      v-slot="{ componentField }"
    >
      <FormItem>
        <FormLabel>{{ contactLabel }}</FormLabel>
        <FormControl>
          <Input v-bind="componentField" data-testid="invite-email-phone-input" />
        </FormControl>
      </FormItem>
    </FormField>
    <div class="flex gap-2 justify-end">
      <Button type="button" variant="ghost" size="sm" class="text-muted-foreground" @click="onCancel">Cancel</Button>
      <Button type="button" size="sm" @click="onSubmit">Add</Button>
    </div>
  </div>
</template>
